// The ERA line matcher.
//
// Remittance on the left, outstanding invoice lines on the right, for one
// account. Arm an ERA line, apply it to invoice lines, confirm. Confirming
// writes a real receipt and ticks the ERA line off in one operation, which is
// the point: before this, the two were separate and an ERA could read as
// allocated with nothing banked.
//
// Two panes side by side do not work on a phone, so below lg the panes become
// steps in a three-step wizard rather than columns. Same reducer, same data,
// different arrangement.
//
// 41-04 ships this read-only: everything renders, arms and targets, but the
// confirm and dismiss actions are wired in 41-05.

import React, { useCallback, useEffect } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import { useLocation } from "react-router-dom";

import {
  createEraMatch,
  dismissEraLines,
  getEraMatch,
  getEraMatchWorkspace,
  undismissEraLines,
  unmatchEraMatch,
} from "../../afinanceapi";
import { zar } from "../Accounts/lineAllocation";
import { Breadcrumb } from "../Breadcrumb/Breadcrumb";
import { canViewAccounts } from "../../utils/permissions";
import InvoicePeek from "../Accounts/InvoicePeek";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import EraDetailModal from "./EraDetailModal";
import EraDismissModal from "./EraDismissModal";
import EraRemittancePane from "./EraRemittancePane";
import EraUnmatchModal from "./EraUnmatchModal";
import OutstandingLinesPane from "./OutstandingLinesPane";
import ShortfallPrompt from "./ShortfallPrompt";
import SurplusPrompt from "./SurplusPrompt";
import useEraMatchModel, { PHASE, eraLineId, targetKey } from "./useEraMatchModel";
import "../Accounts/acc-list.css";

/**
 * Fly a small ghost pill from a clicked row up to its slot in the workbench,
 * so the selection visibly travels to where it is managed. Purely cosmetic:
 * the reducer records the selection immediately and the workbench row is
 * already showing the data by the time the ghost lands. Skipped when either
 * end is hidden (the mobile layout has no workbench) and under
 * prefers-reduced-motion.
 */
function flyToWorkbench(sourceEl, targetSelector, { solid, label }) {
  if (!sourceEl) return;
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  const targetEl = document.querySelector(targetSelector);
  if (!targetEl) return;
  const from = sourceEl.getBoundingClientRect();
  const to = targetEl.getBoundingClientRect();
  if (!from.width || !to.width) return;

  const ghost = document.createElement("div");
  ghost.className = `era-match-fly ${solid ? "era-match-fly-solid" : "era-match-fly-tint"}`;
  ghost.textContent = label;
  ghost.style.left = `${from.left}px`;
  ghost.style.top = `${from.top + from.height / 2 - 14}px`;
  ghost.style.maxWidth = `${Math.min(from.width, 320)}px`;
  document.body.appendChild(ghost);

  const dx = to.left + 8 - from.left;
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  requestAnimationFrame(() => {
    ghost.style.transform = `translate(${dx}px, ${dy}px) scale(0.55)`;
    ghost.style.opacity = "0.3";
  });

  let finished = false;
  const done = () => {
    if (finished) return;
    finished = true;
    ghost.remove();
    // Flash the receiving row, restarting the animation if one is mid-run.
    targetEl.classList.remove("era-workbench-receive");
    void targetEl.offsetWidth;
    targetEl.classList.add("era-workbench-receive");
    setTimeout(() => targetEl.classList.remove("era-workbench-receive"), 700);
  };
  ghost.addEventListener("transitionend", done, { once: true });
  // transitionend does not fire in a background tab; never leave a ghost.
  setTimeout(done, 700);
}

/** The server gates every endpoint; this only decides what the UI offers. */
function callerIsSuperAdmin() {
  try {
    const raw = sessionStorage.getItem("userInfo") || sessionStorage.getItem("user");
    const parsed = raw ? JSON.parse(raw) : null;
    const roles = [
      ...(Array.isArray(parsed?.role) ? parsed.role : parsed?.role ? [parsed.role] : []),
      ...(Array.isArray(parsed?.roles) ? parsed.roles : []),
      ...(Array.isArray(parsed?.permissions) ? parsed.permissions : []),
    ].map(String);
    return roles.includes("SuperAdmin");
  } catch {
    return false;
  }
}

/** Server error codes turned into something a person can act on. */
function describeError(err) {
  const body = err?.response?.data || {};
  switch (body.error) {
    case "over_allocation":
      return `You applied more than the scheme paid on this line (${body.applied} of ${body.budget}).`;
    case "shortfall_action_required":
      return "Every short paid line needs a decision before this can be confirmed.";
    case "line_allocation_unavailable":
      return body.message || "This invoice cannot be paid line by line.";
    case "duplicate_era_line":
      return "That remittance line is already in this receipt.";
    case "duplicate_invoice_line":
      return "That invoice line is already in this receipt.";
    case "invoice_receipts_malformed":
      return `Invoice ${body.invoiceNumber || ""} has malformed receipt data and cannot be paid here until it is repaired.`.replace("  ", " ");
    case "surplus_action_required":
      return "The scheme paid more than this line owes. Decide what happens to the remainder first.";
    case "single_invoice_line_required":
      return "An ERA line is matched to exactly one invoice line.";
    case "era_line_already_matched":
      return "Someone else matched this line while you were working. Reload to see the current state.";
    case "drawn_down":
      return "This receipt's funds were already drawn down elsewhere, so it cannot be reversed.";
    case "no_super_admins_in_practice":
      return "This practice has no SuperAdmin who can approve the un-match.";
    default:
      if (body.ref) {
        return `Something went wrong on the server (ref ${body.ref}). Send that ref to support.`;
      }
      return body.message || body.error || "Something went wrong. Please try again.";
  }
}

const STEPS = [
  { step: 1, label: "Remittance" },
  { step: 2, label: "Apply" },
  { step: 3, label: "Confirm" },
];

export default function EraMatch({ accountId, preArm = null }) {
  const location = useLocation();
  const { state, dispatch, derived } = useEraMatchModel();
  const { toast, showToast, hideToast } = useToast();
  const [peekInvoiceId, setPeekInvoiceId] = React.useState(null);
  // Clicking the right pane with nothing armed pulses the remittance pane:
  // the answer to "why did nothing happen" is pointed at, not written down.
  const [pulseRemittance, setPulseRemittance] = React.useState(false);
  const pulseTimer = React.useRef(null);
  const onNeedArm = React.useCallback(() => {
    setPulseRemittance(false);
    if (pulseTimer.current) clearTimeout(pulseTimer.current);
    // Restart the animation even when it is mid-pulse.
    requestAnimationFrame(() => setPulseRemittance(true));
    pulseTimer.current = setTimeout(() => setPulseRemittance(false), 1600);
  }, []);
  React.useEffect(() => () => pulseTimer.current && clearTimeout(pulseTimer.current), []);

  // Arming fills the right pane; bring the first filled (or suggested) row
  // into view so the connection between the panes is physically visible.
  const armedKeyRef = React.useRef(null);
  const [showDismissModal, setShowDismissModal] = React.useState(false);
  // Claim whose full ERA detail is open in the read-only modal.
  const [eraDetailClaim, setEraDetailClaim] = React.useState(null);
  const [showContinueModal, setShowContinueModal] = React.useState(false);
  const [dismissing, setDismissing] = React.useState(false);
  const [unmatchTarget, setUnmatchTarget] = React.useState(null);
  const [unmatching, setUnmatching] = React.useState(false);

  /**
   * Open the un-match confirmation for one matched line.
   *
   * The receipt number is fetched rather than synthesised from the row: the
   * remittance pane knows the matchId but not the receipt, and a confirmation
   * for an irreversible action has to name the exact receipt it will reverse.
   */
  const openUnmatch = async (line) => {
    if (!line?.matchId) return;
    setUnmatchTarget({ matchId: line.matchId, loading: true });
    try {
      const detail = await getEraMatch(line.matchId);
      setUnmatchTarget({
        matchId: line.matchId,
        loading: false,
        receiptNumber: detail?.receipt?.receiptNumber || null,
        totals: detail?.match?.totals || { applied: line.paidAmount || 0 },
        invoices: detail?.invoices || [],
      });
    } catch (err) {
      // Fall back to what the row knows rather than blocking the un-match on a
      // failed lookup. The amount is still right; only the label is vaguer.
      setUnmatchTarget({
        matchId: line.matchId,
        loading: false,
        receiptNumber: null,
        totals: { applied: line.paidAmount || 0 },
        invoices: [],
      });
    }
  };
  const isSuperAdmin = React.useMemo(callerIsSuperAdmin, []);

  const load = useCallback(async () => {
    dispatch({ type: "LOAD_START" });
    try {
      const workspace = await getEraMatchWorkspace(accountId, preArm);
      dispatch({ type: "LOAD_OK", workspace });
    } catch (err) {
      const status = err?.response?.status;
      dispatch({
        type: "LOAD_FAIL",
        message:
          status === 403
            ? "You do not have access to this account's remittance."
            : err?.response?.data?.error || "Could not load the matching workspace.",
      });
    }
  }, [accountId, preArm, dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  // Escape disarms, so a mis-arm never traps the user in a mode.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && state.armed) dispatch({ type: "DISARM" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.armed, dispatch]);

  const onArm = (claim, line, sourceEl) => {
    // Fly only when this click will actually arm: re-clicking the armed line
    // disarms, and a different ERA is refused while a batch is staged. The
    // checks mirror the reducer's, which stays the authority on what arms.
    const same =
      state.armed &&
      String(state.armed.eraLogId) === String(claim.eraLogId) &&
      String(state.armed.schemeClaimId) === String(claim.schemeClaimId) &&
      String(state.armed.eraLineKey) === String(line.key);
    const blocked =
      state.batch.length > 0 &&
      String(claim.eraLogId) !== String(state.batch[0].eraLogId);
    if (!same && !blocked) {
      flyToWorkbench(sourceEl, ".era-workbench-remit-row", {
        solid: true,
        label: `${line.tariffCode || "Line"}  ${zar.format(line.paidAmount)}`,
      });
    }
    dispatch({ type: "ARM", claim, line });
  };

  React.useEffect(() => {
    const key = state.armed
      ? `${state.armed.eraLogId}:${state.armed.schemeClaimId}:${state.armed.eraLineKey}`
      : null;
    if (!key || key === armedKeyRef.current) {
      armedKeyRef.current = key;
      return;
    }
    armedKeyRef.current = key;
    // Arming can auto-apply a confident suggestion as the target, which fills
    // the workbench's Invoice row with no click on the right pane. Fly a pill
    // from that row too, just after the remittance one, so both sides of the
    // match visibly arrive in the workbench instead of only the left.
    const autoKey = Object.keys(state.targets)[0] || null;
    const autoTarget = autoKey ? state.targets[autoKey] : null;
    // Next frame, so the applied/suggested classes are in the DOM.
    requestAnimationFrame(() => {
      document
        .querySelector(".era-match-applied, .era-match-suggested")
        ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      if (autoTarget) {
        setTimeout(() => {
          flyToWorkbench(
            document.querySelector(`[data-era-row="${autoKey}"]`),
            ".era-workbench-invoice-row",
            {
              solid: false,
              label: `${autoTarget.tariffCode || "Line"}  ${zar.format(
                autoTarget.appliedAmount
              )}`,
            }
          );
        }, 250);
      }
    });
  }, [state.armed, state.targets]);
  const onToggleTarget = (invoice, line, sourceEl) => {
    // Fly only when the click selects; clicking the selected line deselects.
    const adding =
      !!state.armed && !state.targets[targetKey(invoice._id, line.lineNumber)];
    if (adding) {
      flyToWorkbench(sourceEl, ".era-workbench-invoice-row", {
        solid: false,
        label: `${line.tariffCode || "Line"}  ${zar.format(
          Math.min(Number(line.medAidOutstanding) || 0, state.armed.budget)
        )}`,
      });
    }
    dispatch({ type: "TOGGLE_TARGET", invoice, line });
  };
  const onSetSurplusAction = (action) =>
    dispatch({ type: "SET_SURPLUS_ACTION", action });
  const onToggleDismissSelect = (claim, line) =>
    dispatch({ type: "TOGGLE_DISMISS_SELECT", claim, line });

  /**
   * Select every dismissible line on this claim (the one table whose header
   * was clicked, not the whole ERA) and open the dismiss confirmation, so a
   * claim already handled outside this screen comes off the list in one
   * action. The modal lists exactly what will go.
   */
  const onDismissClaim = (claim) => {
    const selection = {};
    for (const line of claim.lines || []) {
      if (line.status !== "unmatched") continue;
      if (derived.stagedEraIds.has(`${claim.eraLogId}:${claim.schemeClaimId}:${line.key}`)) continue;
      selection[eraLineId(claim, line)] = {
        eraLogId: claim.eraLogId,
        schemeClaimId: claim.schemeClaimId,
        eraLineKey: line.key,
        paidAmount: line.paidAmount,
        tariffCode: line.tariffCode,
        patientName: claim.patientName,
      };
    }
    if (Object.keys(selection).length === 0) return;
    dispatch({ type: "SELECT_DISMISS_LINES", selection });
    setShowDismissModal(true);
  };
  const onSetShortfallAction = (key, action, note) =>
    dispatch({ type: "SET_SHORTFALL_ACTION", key, action, note });

  /**
   * Confirm the match. One request writes the receipt and ticks the ERA line
   * off, so there is no window where the money has moved but the ERA has not.
   */
  // Confirming a line STAGES it: nothing is written until Create receipt.
  // The prompt after each confirm asks whether to bank now or keep matching
  // this ERA, which is how one receipt ends up covering many lines.
  const onConfirmLine = () => {
    if (!derived.canConfirm || !state.armed) return;
    dispatch({ type: "STAGE_LINE" });
    setShowContinueModal(true);
  };

  const onCreateReceipt = async () => {
    if (state.batch.length === 0 || state.phase === PHASE.SUBMITTING) return;
    setShowContinueModal(false);
    dispatch({ type: "SUBMIT_START" });
    try {
      const result = await createEraMatch({
        idempotencyKey: state.batchKey,
        accountId,
        practiceNumber: state.workspace?.invoices?.[0]?.practiceNumber,
        // ONE ERA per receipt: every staged pair carries the same eraLogId,
        // enforced at staging time.
        era: { eraLogId: state.batch[0].eraLogId },
        receipt: {},
        matches: state.batch.map((b) => ({
          schemeClaimId: b.schemeClaimId,
          eraLineKey: b.eraLineKey,
          invoiceId: b.invoiceId,
          lineNumber: b.lineNumber,
          paymentAmount: b.appliedAmount,
          ...(b.shortfallAction ? { shortfallAction: b.shortfallAction } : {}),
          ...(b.discountNote ? { discountNote: b.discountNote } : {}),
          ...(b.surplusAction ? { surplusAction: b.surplusAction } : {}),
        })),
      });
      dispatch({ type: "SUBMIT_OK", result });
      const lineCount = result.matchIds?.length || state.batch.length;
      showToast(
        "success",
        `Receipt ${result.receiptNumber} for ${zar.format(result.totals?.applied || 0)} covering ${lineCount} line${lineCount === 1 ? "" : "s"}.`
      );
      dispatch({ type: "RESET" });
      await load();
    } catch (err) {
      dispatch({ type: "SUBMIT_FAIL", message: describeError(err) });
    }
  };

  const onConfirmDismiss = async () => {
    setDismissing(true);
    try {
      await dismissEraLines(
        derived.dismissList.map(({ eraLogId, schemeClaimId, eraLineKey }) => ({
          eraLogId,
          schemeClaimId,
          eraLineKey,
        })),
        accountId
      );
      showToast(
        "success",
        `${derived.dismissCount} line${derived.dismissCount === 1 ? "" : "s"} dismissed.`
      );
      setShowDismissModal(false);
      dispatch({ type: "CLEAR_DISMISS_SELECT" });
      await load();
    } catch (err) {
      dispatch({ type: "SET_ERROR", message: describeError(err) });
      setShowDismissModal(false);
    } finally {
      setDismissing(false);
    }
  };

  const onUndismiss = async (claim, line) => {
    try {
      await undismissEraLines(
        [
          {
            eraLogId: claim.eraLogId,
            schemeClaimId: claim.schemeClaimId,
            eraLineKey: line.key,
          },
        ],
        accountId
      );
      showToast("success", "Line restored to your outstanding list.");
      await load();
    } catch (err) {
      dispatch({ type: "SET_ERROR", message: describeError(err) });
    }
  };

  const onConfirmUnmatch = async (reason) => {
    if (!unmatchTarget) return;
    setUnmatching(true);
    try {
      const result = await unmatchEraMatch(unmatchTarget.matchId, reason);
      showToast(
        "success",
        result.status === "pending_approval"
          ? "Un-match requested. A SuperAdmin must approve it before anything changes."
          : "Un-matched. The receipt is reversed and the line is back on your list."
      );
      setUnmatchTarget(null);
      await load();
    } catch (err) {
      dispatch({ type: "SET_ERROR", message: describeError(err) });
      setUnmatchTarget(null);
    } finally {
      setUnmatching(false);
    }
  };

  if (state.loading) {
    return (
      <Container fluid className="pt-2 acc-list">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading remittance and outstanding invoices...</p>
        </div>
      </Container>
    );
  }

  if (state.loadError) {
    return (
      <Container fluid className="pt-2 acc-list">
        <Alert variant="danger" className="mb-2">
          {state.loadError}
        </Alert>
        <Button variant="outline-secondary" onClick={() => void load()}>
          Try again
        </Button>
      </Container>
    );
  }

  const claims = state.workspace?.eraClaims || [];
  const invoices = state.workspace?.invoices || [];

  // Breadcrumb (D-06): the source page passes its trail via location.state, so
  // arriving from the account reads Accounts > Account > Match ERAs and from
  // an ERA detail reads ERA > [file] > Match ERAs. A deep link or refresh has
  // no state, so fall back to the account route this screen belongs to.
  const accountName = state.workspace?.account?.accountName;
  const breadcrumbItems = [
    ...(location.state?.breadcrumbTrail?.length
      ? location.state.breadcrumbTrail
      : [
          { label: "Accounts", path: "/billing", canAccess: canViewAccounts() },
          {
            label: accountName ? `Account - ${accountName}` : "Account",
            path: `/accounts/${accountId}`,
          },
        ]),
    { label: "Match ERAs" },
  ];

  const remittancePane = (
    <EraRemittancePane
      claims={claims}
      armed={state.armed}
      dismissSelection={state.dismissSelection}
      showDismissed={state.showDismissed}
      dismissedCount={derived.dismissedLines}
      showMatched={state.showMatched}
      matchedCount={derived.matchedLines}
      outstandingCount={derived.outstandingLines}
      stagedIds={derived.stagedEraIds}
      onArm={onArm}
      onToggleDismissSelect={onToggleDismissSelect}
      onDismissClaim={onDismissClaim}
      onOpenEraDetail={setEraDetailClaim}
      onToggleShowDismissed={() => dispatch({ type: "TOGGLE_SHOW_DISMISSED" })}
      onToggleShowMatched={() => dispatch({ type: "TOGGLE_SHOW_MATCHED" })}
      onUnmatch={openUnmatch}
      onUndismiss={onUndismiss}
    />
  );

  const shortfallPrompt = (
    <ShortfallPrompt
      shortfalls={derived.shortfalls}
      onSetAction={onSetShortfallAction}
    />
  );

  const outstandingPane = (
    <OutstandingLinesPane
      invoices={invoices}
      armed={state.armed}
      targets={state.targets}
      suggestions={state.suggestions}
      stagedKeys={derived.stagedTargetKeys}
      onToggleTarget={onToggleTarget}
      onOpenInvoice={(inv) => setPeekInvoiceId(inv._id)}
      onNeedArm={onNeedArm}
    />
  );

  // The full armed remittance line, for the workbench's detail row. The armed
  // state only carries the keys and the budget.
  const armedClaim = state.armed
    ? claims.find(
        (c) =>
          String(c.eraLogId) === String(state.armed.eraLogId) &&
          String(c.schemeClaimId) === String(state.armed.schemeClaimId)
      )
    : null;
  const armedLine =
    armedClaim?.lines?.find(
      (l) => String(l.key) === String(state.armed?.eraLineKey)
    ) || null;

  const fmtWorkbenchDate = (raw) => {
    if (!raw) return "";
    const str = String(raw);
    const iso = /^\d{8}$/.test(str)
      ? `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`
      : str;
    // Bare dates pinned to local midnight, same reasoning as the panes.
    const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00` : iso);
    return Number.isNaN(d.getTime()) ? str : d.toLocaleDateString("en-GB");
  };

  const workbenchTargets = Object.values(state.targets);

  // The workbench: what is selected on each side, the shortfall decision, and
  // the actions, pinned across the top so the whole match reads in one place.
  const workbench = (
    <div className="era-match-workbench">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <span className="fw-semibold text-primary">
          {derived.dismissCount > 0
            ? `${derived.dismissCount} line${
                derived.dismissCount === 1 ? "" : "s"
              } selected to dismiss`
            : state.armed
            ? state.armed.patientName || "Patient"
            : "No remittance line selected"}
        </span>
        <span className="d-flex gap-2 align-items-center">
          {state.armed && (
            <span className="small text-muted me-2">
              {zar.format(derived.applied)} of {zar.format(derived.budget)} applied
              {derived.remaining > 0.005
                ? `, ${zar.format(derived.remaining)} left`
                : ""}
            </span>
          )}
          {derived.dismissCount > 0 && (
            <span className="small text-muted me-2">
              {zar.format(derived.dismissTotal)}
            </span>
          )}
        </span>
      </div>

      {state.batch.length > 0 && (
        <div className="era-match-staged mb-2">
          <span className="small fw-semibold text-primary d-block mb-1">
            Confirmed for this receipt
          </span>
          {state.batch.map((b, i) => (
            <div
              key={`${b.schemeClaimId}:${b.eraLineKey}`}
              className="d-flex align-items-center gap-2 small"
            >
              <span>
                {b.tariffCode || "-"} {b.patientName ? `(${b.patientName})` : ""} to{" "}
                {b.invoiceNumber} line {b.lineNumber}
              </span>
              <span className="acc-list-num fw-semibold">
                {zar.format(b.appliedAmount)}
              </span>
              {b.surplus > 0 && (
                <span className="text-muted">
                  +{zar.format(b.surplus)}{" "}
                  {b.surplusAction === "unallocated" ? "to unallocated" : "ignored"}
                </span>
              )}
              <OverlayTrigger placement="top" overlay={<Tooltip>Remove from this receipt</Tooltip>}>
                <button
                  type="button"
                  className="btn btn-link text-secondary text-decoration-none p-0 border-0"
                  style={{ width: 20, height: 20, lineHeight: 1 }}
                  aria-label={`Remove staged line ${b.tariffCode}`}
                  onClick={() => dispatch({ type: "UNSTAGE_LINE", index: i })}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </OverlayTrigger>
            </div>
          ))}
          <div className="small text-muted mt-1">
            Receipt total {zar.format(derived.batchBanked)}
          </div>
        </div>
      )}

      {(() => {
        const target = workbenchTargets[0] || null;
        const shortfall = derived.shortfalls[0] || null;
        const numCell = "acc-list-num align-middle";
        return (
          <Table borderless size="sm" className="w-auto mb-0 era-match-workbench-table">
            <thead>
              <tr>
                <th />
                <th className="small text-primary">Service date</th>
                <th className="small text-primary">Tariff</th>
                <th className="small text-primary acc-list-num">Claimed / owing</th>
                <th className="small text-primary acc-list-num">Paid</th>
              </tr>
            </thead>
            <tbody>
              <tr className="era-workbench-remit-row">
                <td className="fw-semibold text-primary align-middle">Remittance</td>
                {armedLine ? (
                  <>
                    <td className="align-middle">
                      {fmtWorkbenchDate(armedLine.dateOfService) || "-"}
                    </td>
                    <td className="align-middle">{armedLine.tariffCode || "-"}</td>
                    <td className={numCell}>{zar.format(armedLine.claimedAmount)}</td>
                    <td className={`${numCell} fw-semibold`}>
                      {zar.format(armedLine.paidAmount)}
                    </td>
                  </>
                ) : (
                  <td colSpan={4} className="text-muted align-middle">
                    Select a line in the remittance pane.
                  </td>
                )}
              </tr>
              <tr className="era-workbench-invoice-row">
                <td className="fw-semibold text-primary align-middle">Invoice</td>
                {target ? (
                  <>
                    <td className="align-middle">
                      {fmtWorkbenchDate(target.lineDate) || "-"}
                    </td>
                    <td className="align-middle">{target.tariffCode || "-"}</td>
                    <td className={numCell}>{zar.format(target.maxAmount)}</td>
                    <td className={`${numCell} fw-semibold`}>
                      {zar.format(target.appliedAmount)}
                    </td>
                  </>
                ) : (
                  <td colSpan={4} className="text-muted align-middle">
                    {state.armed
                      ? "Select an invoice line in the outstanding pane."
                      : "Waiting on a remittance line."}
                  </td>
                )}
              </tr>
              {shortfall && (
                <tr>
                  <td className="fw-semibold align-middle" style={{ color: "#b02a37" }}>
                    Shortfall
                  </td>
                  <td colSpan={3} />
                  <td className={`${numCell} fw-semibold`} style={{ color: "#b02a37" }}>
                    {zar.format(shortfall.shortfall)}
                  </td>
                </tr>
              )}
              {derived.surplus > 0 && (
                <tr>
                  <td className="fw-semibold align-middle" style={{ color: "#b02a37" }}>
                    Overpaid
                  </td>
                  <td colSpan={3} />
                  <td className={`${numCell} fw-semibold`} style={{ color: "#b02a37" }}>
                    {zar.format(derived.surplus)}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        );
      })()}

      {derived.shortfalls.length > 0 && (
        <div className="era-match-workbench-shortfall mt-2">
          <ShortfallPrompt
            shortfalls={derived.shortfalls}
            onSetAction={onSetShortfallAction}
            standalone={false}
          />
        </div>
      )}
      {derived.surplus > 0 && (
        <div className="era-match-workbench-shortfall mt-2">
          <SurplusPrompt
            surplus={derived.surplus}
            action={derived.surplusAction}
            onSetAction={onSetSurplusAction}
          />
        </div>
      )}
    </div>
  );

  /* Arming and dismissing are mutually exclusive modes, so the same action
     column serves both: Confirm line / Cancel while matching, Dismiss
     selected / Clear while a dismiss selection is live. Stacked one per row
     beside the workbench summary. */
  const workbenchActions = (
    <div className="d-flex flex-column gap-2 align-items-start">
      {derived.dismissCount > 0 ? (
        <Button
          variant="outline-danger"
          size="sm"
          style={{ minWidth: 170 }}
          onClick={() => setShowDismissModal(true)}
        >
          <i className="fa-solid fa-eye-slash me-2" />
          Dismiss selected
        </Button>
      ) : (
        <Button
          variant="primary"
          size="sm"
          style={{ minWidth: 170 }}
          disabled={!derived.canConfirm}
          onClick={onConfirmLine}
        >
          Confirm line
        </Button>
      )}
      {derived.dismissCount > 0 ? (
        <Button
          variant="outline-secondary"
          size="sm"
          style={{ minWidth: 170 }}
          onClick={() => dispatch({ type: "CLEAR_DISMISS_SELECT" })}
        >
          Clear
        </Button>
      ) : (
        <Button
          variant="outline-secondary"
          size="sm"
          style={{ minWidth: 170 }}
          disabled={!state.armed}
          onClick={() => dispatch({ type: "DISARM" })}
        >
          Cancel
        </Button>
      )}
      {derived.batchCount > 0 && (
        <Button
          variant="success"
          size="sm"
          style={{ minWidth: 170 }}
          disabled={!derived.canCreateReceipt}
          onClick={onCreateReceipt}
        >
          {state.phase === PHASE.SUBMITTING ? (
            <Spinner animation="border" size="sm" role="status" />
          ) : (
            `Create receipt (${derived.batchCount})`
          )}
        </Button>
      )}
    </div>
  );

  // The armed line's running total. Also the mobile step-2 header, so the
  // budget stays visible while scrolling a long invoice list.
  const armedBar = state.armed && (
    <div className="era-match-armed-bar d-flex align-items-center justify-content-between flex-wrap gap-2">
      <span className="fw-semibold text-primary">
        {state.armed.patientName || "Armed line"}
        <span className="ms-2 small">{state.armed.tariffCode}</span>
      </span>
      <span className="small">
        {zar.format(derived.applied)} of {zar.format(derived.budget)} applied,{" "}
        {zar.format(derived.remaining)} left
      </span>
      <Button
        size="sm"
        variant="outline-secondary"
        onClick={() => dispatch({ type: "DISARM" })}
      >
        Change
      </Button>
    </div>
  );

  const footer = (
    <div className="era-match-footer d-flex align-items-center justify-content-between flex-wrap gap-2">
      {derived.dismissCount > 0 ? (
        <>
          <span className="fw-semibold">
            {derived.dismissCount} line{derived.dismissCount === 1 ? "" : "s"}{" "}
            selected, {zar.format(derived.dismissTotal)}
          </span>
          <span className="d-flex gap-2">
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => setShowDismissModal(true)}
            >
              <i className="fa-solid fa-eye-slash me-2" />
              Dismiss selected
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => dispatch({ type: "CLEAR_DISMISS_SELECT" })}
            >
              Clear
            </Button>
          </span>
        </>
      ) : state.armed ? (
        <>
          <span className="fw-semibold">
            {zar.format(derived.applied)} of {zar.format(derived.budget)} applied
            {derived.remaining > 0.005 && (
              <span className="text-muted ms-2">
                {zar.format(derived.remaining)} left
              </span>
            )}
          </span>
          <span className="d-flex gap-2 align-items-center">
            {derived.undecided.length > 0 && (
              <Button
                variant="link"
                size="sm"
                className="text-decoration-none p-0"
                onClick={() => dispatch({ type: "SET_MOBILE_STEP", step: 3 })}
              >
                {derived.undecided.length} shortfall
                {derived.undecided.length === 1 ? "" : "s"} need a decision
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              disabled={!derived.canConfirm}
              onClick={onConfirmLine}
            >
              Confirm line
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => dispatch({ type: "DISARM" })}
            >
              Cancel
            </Button>
          </span>
        </>
      ) : derived.batchCount > 0 ? (
        <>
          <span className="fw-semibold">
            {derived.batchCount} line{derived.batchCount === 1 ? "" : "s"} confirmed,{" "}
            {zar.format(derived.batchBanked)}
          </span>
          <Button
            variant="success"
            size="sm"
            disabled={!derived.canCreateReceipt}
            onClick={onCreateReceipt}
          >
            {state.phase === PHASE.SUBMITTING ? (
              <Spinner animation="border" size="sm" role="status" />
            ) : (
              "Create receipt"
            )}
          </Button>
        </>
      ) : (
        <span className="text-muted">
          Select a remittance line to start matching.
        </span>
      )}
    </div>
  );

  return (
    <Container fluid className="pt-2 acc-list">
      {/* Above the sticky block, so it scrolls away and the pinned workbench
          keeps its vertical space for the match itself. The pt-2 tops this
          container's pt-2 up to the account page's pt-3, so the breadcrumb
          lands at the same height on both screens. */}
      <div className="ms-1 mb-2 pt-2">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="era-match-sticky-top">
      {/* The way back to the account is the breadcrumb above. */}
      <div className="text-center mb-2">
        <h5 className="text-primary mb-1">
          Match ERAs
          {state.workspace?.account?.accountName && (
            <span
              className="ms-2 small fw-semibold"
              style={{ color: "var(--acc-brand-tint)" }}
            >
              {state.workspace.account.accountName}
            </span>
          )}
        </h5>
        {!state.armed && (
          <p className="small text-muted mb-0">
            Pick a remittance line to start matching.
          </p>
        )}
      </div>
      <div className="d-none d-lg-block">
        <Row>
          <Col xs={12} xl={4}>{workbench}</Col>
          <Col xs={12} xl={8} className="d-flex align-items-start">
            {workbenchActions}
          </Col>
        </Row>
      </div>
      </div>

      {state.error && (
        <Alert variant="danger" className="mb-2">
          {state.error}
        </Alert>
      )}

      {/* Announced to screen readers without stealing focus. */}
      <div className="visually-hidden" aria-live="polite">
        {state.armed
          ? `Armed ${state.armed.tariffCode}, ${zar.format(derived.remaining)} remaining of ${zar.format(derived.budget)}`
          : "No remittance line armed"}
      </div>

      {/* Desktop: the two panes; the heading + workbench block above is the
          sticky unit they scroll underneath. */}
      <div className="d-none d-lg-block">
        <Row>
          <Col xs={12} xl={5} className={pulseRemittance ? "era-match-pulse" : ""}>
            {remittancePane}
          </Col>
          <Col xs={12} xl={7}>
            {outstandingPane}
          </Col>
        </Row>
      </div>

      {/* Mobile: the same panes as steps. */}
      <div className="d-lg-none">
        <div className="d-flex gap-2 mb-2">
          {STEPS.map((s) => (
            <Button
              key={s.step}
              size="sm"
              style={{ minWidth: 100 }}
              variant={state.mobileStep === s.step ? "primary" : "outline-primary"}
              onClick={() => dispatch({ type: "SET_MOBILE_STEP", step: s.step })}
              disabled={s.step > 1 && !state.armed}
            >
              {s.step} {s.label}
            </Button>
          ))}
        </div>

        {state.mobileStep === 1 && remittancePane}
        {state.mobileStep === 2 && (
          <>
            {armedBar}
            {outstandingPane}
          </>
        )}
        {state.mobileStep === 3 && (
          <>
            {armedBar}
            {derived.shortfalls.length === 0 && derived.surplus <= 0 ? (
              <p className="text-center py-4">
                Nothing short or over paid. Confirm to write the receipt.
              </p>
            ) : (
              <>
                {shortfallPrompt}
                <SurplusPrompt
                  surplus={derived.surplus}
                  action={derived.surplusAction}
                  onSetAction={onSetSurplusAction}
                />
              </>
            )}
          </>
        )}
        {footer}
      </div>

      <InvoicePeek
        invoiceId={peekInvoiceId}
        show={!!peekInvoiceId}
        onHide={() => setPeekInvoiceId(null)}
      />

      <Modal show={showContinueModal} onHide={() => setShowContinueModal(false)} centered>
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">Line confirmed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">
            {derived.batchCount} line{derived.batchCount === 1 ? "" : "s"} confirmed on
            this ERA, {zar.format(derived.batchBanked)} in total.
          </p>
          <p className="small text-muted mb-0">
            Create the receipt now, or keep matching more lines on this ERA. One
            receipt covers everything you confirm here.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            style={{ minWidth: 160 }}
            onClick={onCreateReceipt}
          >
            Create receipt now
          </Button>
          <Button
            variant="outline-secondary"
            style={{ minWidth: 160 }}
            onClick={() => setShowContinueModal(false)}
          >
            Continue matching
          </Button>
        </Modal.Footer>
      </Modal>

      <EraDetailModal
        show={!!eraDetailClaim}
        onHide={() => setEraDetailClaim(null)}
        eraLogId={eraDetailClaim?.eraLogId}
        schemeClaimId={eraDetailClaim?.schemeClaimId}
        patientName={eraDetailClaim?.patientName}
      />

      <EraDismissModal
        show={showDismissModal}
        onHide={() => setShowDismissModal(false)}
        lines={derived.dismissList}
        total={derived.dismissTotal}
        submitting={dismissing}
        onConfirm={onConfirmDismiss}
      />

      {unmatchTarget && !unmatchTarget.loading && (
        <EraUnmatchModal
          show
          onHide={() => setUnmatchTarget(null)}
          match={unmatchTarget}
          isSuperAdmin={isSuperAdmin}
          submitting={unmatching}
          onConfirm={onConfirmUnmatch}
        />
      )}

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />
    </Container>
  );
}
